import { useState, useEffect, useRef } from 'react';
import { DiwaliBadge } from './DiwaliBadge';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, BellRing, Waves, Layers, Gauge, BadgeCheck, Sparkles } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { ProductWordmark } from './ProductWordmark';
import { PaddleStage } from './PaddleStage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const BLUE = '#1E6FD9';
// "ProFoam" wordmark geometry: land on the F (0.415–0.547), step down onto "oam" x-height

// Original untouched ProFoam studio photos
const VIEWS = [
  { key: 'pf_full', label: 'Full view', src: `${API}/static/products/profoam_n1.png` },
  { key: 'pf_face', label: 'Face', src: `${API}/static/products/profoam_n2.png` },
  { key: 'pf_angle', label: 'Angle', src: `${API}/static/products/profoam_n3.png` },
  { key: 'pf_grip', label: 'Grip', src: `${API}/static/products/profoam_n4.png` },
  { key: 'pf_side', label: 'Side profile', src: `${API}/static/products/profoam_n5.png` },
];

const SPECS = [
  { Icon: Waves, label: 'Core', value: '16mm ProFoam · Damped' },
  { Icon: Layers, label: 'Face', value: 'Toray T700 Carbon Fibre' },
  { Icon: Gauge, label: 'Weight', value: '7.6 – 8.0 oz' },
  { Icon: BadgeCheck, label: 'Certified', value: 'USA Pickleball Approved' },
];

const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

const useReveal = () => {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, shown];
};

export const ProFoamShowcase = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [ref, shown] = useReveal();

  useEffect(() => {
    fetch(`${API}/products/profoam/teaser`)
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => { if (p) setProduct(p); })
      .catch(() => {});
  }, []);

  const price = product;
  const published = product?.live === true && product?.hidden !== true;
  const soldOut = published && product?.in_stock === false;
  const isLive = published && !soldOut;
  const target = '/product/profoam';

  const reveal = (delay) => ({
    opacity: shown ? 1 : 0,
    transform: shown ? 'translateY(0)' : 'translateY(30px)',
    transition: `opacity 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}ms, transform 0.9s cubic-bezier(0.22,1,0.36,1) ${delay}ms`,
  });

  return (
    <section
      id="profoam"
      ref={ref}
      className="relative overflow-hidden isolate bg-[#F4F7FB] border-t border-[#E3E9F2]"
      aria-label="DYSSC ProFoam Pro — coming soon"
      data-testid="profoam-showcase"
    >
      <div className="absolute inset-0 pointer-events-none opacity-[0.03]" style={{ backgroundImage: NOISE }} />
      {/* cool blue studio wash */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 55% at 28% 55%, rgba(30,111,217,0.10) 0%, rgba(30,111,217,0) 70%)' }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 md:pt-14 pb-12 md:pb-16 relative" data-testid="profoam-card">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-5 lg:gap-y-0 lg:gap-x-6">

          {/* ---- Header (desktop: right column, top) ---- */}
          <div className="lg:col-span-5 lg:col-start-8 lg:row-start-1 lg:self-end relative z-20">
            <div className="flex items-center gap-3 flex-wrap" style={reveal(80)}>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] font-bold" style={{ color: BLUE }}>
                New · USA Pickleball Approved
              </p>
              {!published && (
                <span
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold tracking-[0.2em] uppercase text-white shadow-[0_6px_18px_rgba(30,111,217,0.35)]"
                  style={{ backgroundColor: BLUE }}
                  data-testid="profoam-coming-soon-badge"
                >
                  <Sparkles className="w-3 h-3" /> Coming soon
                </span>
              )}
              {soldOut && (
                <span
                  className="px-2 py-0.5 rounded-full text-[9px] font-bold tracking-widest text-[#E02128] bg-[#E02128]/10 border border-[#E02128]/40"
                  data-testid="profoam-sold-out-badge"
                >
                  SOLD OUT
                </span>
              )}
            </div>

            <h2 className="mt-5" style={reveal(160)}>
              <img src={`${API}/static/brand/dyssc_wordmark_dark.png`} alt="DYSSC" className="h-4 md:h-5 w-auto" />
              <span className="relative inline-block">
                <ProductWordmark
                  slug="profoam"
                  name="DYSSC ProFoam Pro"
                  imgClassName="h-12 md:h-14 mt-2"
                  fallbackClassName="font-display text-5xl md:text-7xl text-[#0A0A0A] uppercase tracking-tight"
                />
              </span>
            </h2>
            <p className="mt-3 text-[#5B6675] text-base md:text-lg leading-relaxed max-w-md" style={reveal(240)}>
              Shock-dispersing ProFoam core. Soaks up impact, hands back touch —
              surgical control and spin for finesse players, wrapped in aerospace-grade Toray T700 carbon fibre.
            </p>
          </div>

          {/* ---- Interactive stage (desktop: left) ---- */}
          <div className="lg:col-span-7 lg:col-start-1 lg:row-start-1 lg:row-span-2 lg:self-center relative" style={reveal(0)}>
            <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none select-none z-0" aria-hidden="true">
              <span
                className="font-display font-black uppercase leading-none tracking-tighter text-[clamp(80px,10.5vw,168px)] text-transparent"
                style={{ WebkitTextStroke: '1.5px #D6DFEC' }}
              >
                PRO
              </span>
              <span className="font-display font-black uppercase leading-none tracking-tighter text-[clamp(80px,10.5vw,168px)] text-[#E6EDF7]">
                FOAM
              </span>
            </div>

            <div className="relative z-10">
              <PaddleStage
                views={VIEWS}
                alt="DYSSC ProFoam Pro pickleball paddle"
                autoCycle={shown}
                showHint
                accent={BLUE}
                onStageClick={() => navigate(target)}
                heightClass="h-[420px] md:h-[600px]"
              />
            </div>
          </div>

          {/* ---- Price, specs & CTA (desktop: right column, bottom) ---- */}
          <div className="lg:col-span-5 lg:col-start-8 lg:row-start-2 lg:self-start relative z-20">
            <div className="lg:mt-6 lg:pt-5 lg:border-t border-[#D9E2EE]" style={reveal(320)}>
              {published ? (
                <>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">Price · Incl. GST</p>
                  <div className="mt-2 flex items-end gap-3 flex-wrap">
                    <span className="font-mono text-4xl md:text-5xl text-[#0A0A0A]" data-testid="profoam-price">
                      ₹{price.priceINR.toLocaleString('en-IN')}
                    </span>
                    {price.compareAtINR > price.priceINR && (
                      <>
                        <span className="font-mono text-xl text-neutral-400 line-through mb-1" data-testid="profoam-compare-price">
                          ₹{price.compareAtINR.toLocaleString('en-IN')}
                        </span>
                        <span
                          className="mb-1.5 inline-flex items-center px-3 py-1 rounded-full text-[11px] font-bold tracking-wider text-white bg-[length:200%_100%] animate-[gradient-x_2.5s_linear_infinite] shadow-[0_4px_16px_rgba(30,111,217,0.35)]"
                          style={{ backgroundImage: `linear-gradient(90deg, ${BLUE}, #4F97FF, ${BLUE})` }}
                        >
                          {price.discountPercent}% OFF
                        </span>
                      </>
                    )}
                    <DiwaliBadge accent={BLUE} className="mb-1.5" testId="diwali-offer-badge-profoam" />
                  </div>
                </>
              ) : (
                <>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400">Availability</p>
                  <div className="mt-2 flex items-end gap-3 flex-wrap">
                    <span className="font-display text-4xl md:text-5xl uppercase tracking-tight" style={{ color: BLUE }} data-testid="profoam-coming-soon-text">
                      Coming soon
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-px bg-[#D9E2EE] border border-[#D9E2EE]" style={reveal(400)} data-testid="profoam-specs">
              {SPECS.map(({ Icon, label, value }) => (
                <div key={label} className="bg-white p-4 group hover:bg-[#F7FAFE] transition-colors duration-300">
                  <div className="flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.8} style={{ color: BLUE }} />
                    <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">{label}</span>
                  </div>
                  <p className="mt-1.5 text-sm font-semibold text-[#0A0A0A] leading-snug">{value}</p>
                </div>
              ))}
            </div>

            <div className="mt-6" style={reveal(480)}>
              <div className="flex items-center gap-4 flex-wrap">
                <button
                  onClick={() => {
                    if (isLive && product) { addToCart(product, 1); return; }
                    navigate(soldOut ? '/notify/profoam' : target);
                  }}
                  className="inline-flex items-center justify-center gap-2 w-[212px] px-9 py-4 rounded-full text-white font-bold uppercase tracking-[0.15em] text-sm transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] shadow-[0_12px_30px_rgba(30,111,217,0.30)] hover:shadow-[0_0_24px_rgba(30,111,217,0.5)] hover:brightness-110"
                  style={{ backgroundColor: BLUE }}
                  data-testid="profoam-notify-button"
                >
                  {isLive ? 'Add to Cart' : (<><BellRing className="w-4 h-4" /> Notify Me</>)}
                </button>
                <button
                  onClick={() => navigate('/product/profoam')}
                  className="group inline-flex items-center gap-2 text-[#0A0A0A] hover:text-[#1E6FD9] text-sm font-semibold uppercase tracking-[0.15em] transition-colors duration-200"
                  data-testid="profoam-view-details"
                >
                  Full details
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
